import { EASE, lerp, lerpColor, randRange } from './ease.js';
import { PRESETS, type EmitterPreset, type ParticleTexture, type PresetName } from './presets.js';

/**
 * The particle pool (TRD-04 §3).
 *
 * Deliberately hand-rolled and deliberately free of Pixi. Two reasons:
 *
 * 1. Pixi's emitters are heavier than this needs to be and much harder to hold to a
 *    per-frame budget.
 * 2. Keeping the simulation of particles separate from their drawing is what lets the
 *    determinism test (AC-1) run headless — it can pump thousands of particles through a
 *    real world's event stream under Node and prove the sim hash never moves.
 *
 * Positions are in TILE units, matching the sim, so emitters can be handed entity
 * coordinates directly. The renderer multiplies by TILE_PX.
 */

export interface Particle {
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  sizeEnd: number;
  rotation: number;
  vRotation: number;
  tintStart: number;
  tintEnd: number;
  alphaStart: number;
  alphaEnd: number;
  drag: number;
  gravity: number;
  texture: ParticleTexture;
  blend: 'normal' | 'add';
  ease: EmitterPreset['ease'];

  // Derived each frame, read by the renderer.
  currentSize: number;
  currentTint: number;
  currentAlpha: number;
}

/**
 * Hard ceiling (R-04-03). When full, the OLDEST particle is overwritten rather than the
 * spawn being dropped — a wave-25 boss death should degrade by shortening its tail, not
 * by silently losing its opening burst.
 *
 * Raised from 4096 when every event got an effect. The pool is a flat pre-allocated array
 * walked once per frame, so the cost of headroom is memory rather than time: the update
 * loop skips inactive slots, and the renderer only touches live ones.
 */
export const MAX_PARTICLES = 12288;

function makeParticle(): Particle {
  return {
    active: false,
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    life: 0,
    maxLife: 1,
    size: 1,
    sizeEnd: 0,
    rotation: 0,
    vRotation: 0,
    tintStart: 0xffffff,
    tintEnd: 0xffffff,
    alphaStart: 1,
    alphaEnd: 0,
    drag: 1,
    gravity: 0,
    texture: 'p_dot1',
    blend: 'normal',
    ease: 'linear',
    currentSize: 1,
    currentTint: 0xffffff,
    currentAlpha: 1,
  };
}

/**
 * Per-emission overrides. Everything else about an effect lives in its preset, which is
 * data so the tuning panel can rewrite it live (R-04-07); these are the four things that
 * genuinely vary per event — where the blast was, how big it was, which way it pointed,
 * and whose colour it wears.
 */
export interface EmitOptions {
  /** Radians. Cone presets spread around this; radial presets ignore it. */
  direction?: number;
  /**
   * Used when the preset declares `tint: 'source'` — normally the class colour of
   * whatever fired, which is what ties an effect to its cause on a board where every
   * tower is the same shape.
   */
  sourceTint?: number;
  /** Multiplies the preset's particle count. Zero emits nothing at all. */
  countScale?: number;
  /** Scatter spawn positions over a disc of this radius, in TILES. */
  spread?: number;
  /** Multiplies particle size — how a blast gets drawn at the radius it actually had. */
  sizeScale?: number;
  /** Multiplies launch speed, for the same reason. */
  speedScale?: number;
}

export class ParticleSystem {
  readonly particles: Particle[] = Array.from({ length: MAX_PARTICLES }, makeParticle);

  private cursor = 0;
  private liveCount = 0;

  /** Live particles, for the debug overlay and the performance budget check. */
  get live(): number {
    return this.liveCount;
  }

  /** Emit a burst. Everything not overridden in `options` comes from the preset. */
  emit(name: PresetName, x: number, y: number, options: EmitOptions = {}): void {
    const preset = PRESETS[name] as EmitterPreset;
    const [minCount, maxCount] = preset.count;
    const scale = options.countScale ?? 1;
    // `countScale: 0` means NOTHING, not one. It used to floor to a single particle, so
    // an effect deliberately scaled to nothing still put one lonely mote on the board.
    if (scale <= 0) return;
    const count = Math.max(1, Math.round(randRange(minCount, maxCount + 1) * scale));

    const spread = options.spread ?? 0;
    const sizeScale = options.sizeScale ?? 1;
    const speedScale = options.speedScale ?? 1;
    const direction = options.direction ?? 0;
    const sourceTint = options.sourceTint ?? 0xffffff;

    for (let i = 0; i < count; i++) {
      // Scattered over a disc rather than fired from one point, for anything that happens
      // over an AREA — a blast, a zone tick. The sqrt keeps the scatter even instead of
      // crowding everything into the centre.
      let px = x;
      let py = y;
      if (spread > 0) {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.sqrt(Math.random()) * spread;
        px += Math.cos(angle) * radius;
        py += Math.sin(angle) * radius;
      }
      this.spawnOne(preset, px, py, direction, sourceTint, sizeScale, speedScale);
    }
  }

  private spawnOne(
    preset: EmitterPreset,
    x: number,
    y: number,
    direction: number,
    sourceTint: number,
    sizeScale: number,
    speedScale: number,
  ): void {
    const p = this.particles[this.cursor] as Particle;
    this.cursor = (this.cursor + 1) % MAX_PARTICLES;
    if (!p.active) this.liveCount++;

    const angle =
      preset.cone !== undefined
        ? direction + randRange(-preset.cone, preset.cone)
        : randRange(preset.angle[0], preset.angle[1]);
    const speed = randRange(preset.speed[0], preset.speed[1]) * speedScale;

    p.active = true;
    p.x = x;
    p.y = y;
    p.vx = Math.cos(angle) * speed;
    p.vy = Math.sin(angle) * speed;
    p.maxLife = randRange(preset.life[0], preset.life[1]);
    p.life = p.maxLife;
    p.size = randRange(preset.size[0], preset.size[1]) * sizeScale;
    p.sizeEnd = preset.sizeEnd * sizeScale;
    p.rotation = Math.random() * Math.PI * 2;
    p.vRotation = randRange(-0.01, 0.01);
    p.tintStart =
      preset.tint === 'source'
        ? sourceTint
        : (preset.tint[Math.floor(Math.random() * preset.tint.length)] ?? 0xffffff);
    p.tintEnd = preset.tintEnd;
    p.alphaStart = preset.alphaStart;
    p.alphaEnd = preset.alphaEnd;
    p.drag = preset.drag;
    p.gravity = preset.gravity;
    p.texture = preset.texture;
    p.blend = preset.blend;
    p.ease = preset.ease;
    p.currentSize = p.size;
    p.currentTint = p.tintStart;
    p.currentAlpha = p.alphaStart;
  }

  /**
   * Advance by REAL elapsed time (R-04-02).
   *
   * Never scaled by the sim speed multiplier. At 4× the system receives four times as many
   * spawn events, which is the correct way for it to feel busier — multiplying lifetimes
   * as well turns the screen to soup.
   *
   * A flat array loop with no allocation, no closures and no method dispatch: the budget
   * is 2 ms at 2000 live particles (R-04-10).
   */
  update(deltaMs: number): void {
    if (deltaMs <= 0) return;
    const dt = deltaMs / 16.6667;
    let live = 0;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this.particles[i] as Particle;
      if (!p.active) continue;

      p.life -= deltaMs;
      if (p.life <= 0) {
        p.active = false;
        p.currentAlpha = 0;
        continue;
      }
      live++;

      p.vy += p.gravity * dt;
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx * dt * 0.06;
      p.y += p.vy * dt * 0.06;
      p.rotation += p.vRotation * dt;

      const t = EASE[p.ease](1 - p.life / p.maxLife);
      p.currentSize = lerp(p.size, p.sizeEnd, t);
      p.currentAlpha = lerp(p.alphaStart, p.alphaEnd, t);
      p.currentTint = lerpColor(p.tintStart, p.tintEnd, t);
    }

    this.liveCount = live;
  }

  clear(): void {
    for (const p of this.particles) {
      p.active = false;
      p.currentAlpha = 0;
    }
    this.liveCount = 0;
  }
}
